
  import { defineComponent, onMounted, ref, reactive, computed } from 'vue';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import { reinitializeComponents } from '@/core/plugins/keenthemes';
  import moment, { max } from 'moment';
  import Multiselect from '@vueform/multiselect';
  import { StatusNotesLabels } from '../registered-trades/status-notes.enum';
  import {
    TradeStatusMap,
    TradeStatusLabels,
  } from '../registered-trades/status-map.enum';

  export default defineComponent({
    name: 'registered-trades-per-quarter-reports',
    components: { Multiselect },
    async setup() {
      const showExportBtn = ref(false);
      const data = ref();
      const isLoading = ref(false);
      const hasQueried = ref(false);
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const submitButton = ref<HTMLButtonElement | null>(null);
      const { can } = useAbility();
      const selectedItems = ref<any>({
        dateFrom: '',
        dateTo: '',
        status: [],
        statusNotesIds: [],
        selectedPropertyStatus: [],
        includedAreas: [],
        selectedArea: [],
        selectedPropertyType: [],
        propertyGroupingId: '',
        selectedGov: [],
      });

      const propertyGroupings = computed(
        () => store.getters.allListRegisteredTradesGroupings
      );

      const statusOptions = Object.entries(TradeStatusMap).map(
        ([label, value]) => ({ value, label })
      );

      const govs = computed(() => store.getters.allListGov);

      const statusNotesOptions = Object.entries(StatusNotesLabels).map(
        ([value, label]) => ({ value: Number(value), label })
      );

      const registeredTradesDesc = computed(
        () => store.getters.getRegisteredDesc
      );
      const areasComputed = computed(() => store.getters.allListAreas);
      const propertyTypesComputed = computed(
        () => store.getters.propertyTypesAllList
      );

      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const goBack = () => {
        router.back();
      };

      const resetFilters = () => {
        selectedItems.value = {
          dateFrom: '',
          dateTo: '',
          status: [],
          statusNotesIds: [],
          selectedGov: [],
          selectedPropertyStatus: [],
          includedAreas: [],
          selectedArea: [],
          selectedPropertyType: [],
          propertyGroupingId: '',
        };
        showExportBtn.value = false;
        data.value = undefined;
        hasQueried.value = false;
      };

      // await store.dispatch(Actions.GET_REGISTERED_TRADE_REPORTS);

      // data.value = store.getters.registeredTradesDashboard;

      const onSubmitCreate = async () => {
        if (selectedItems.value.dateFrom) {
          selectedItems.value.dateFrom = moment(
            selectedItems.value.dateFrom
          ).format('YYYY-MM-DD');
        }
        if (selectedItems.value.dateTo) {
          selectedItems.value.dateTo = moment(
            selectedItems.value.dateTo
          ).format('YYYY-MM-DD');
        }
        console.log(selectedItems.value);

        if (selectedItems.value.dateFrom && selectedItems.value.dateTo) {
          if (
            moment(selectedItems.value.dateTo).isBefore(
              moment(selectedItems.value.dateFrom)
            )
          ) {
            Swal.fire({
              text: translate('dateToMustBeAfterDateFrom'),
              icon: 'warning',
              confirmButtonText: translate('ok'),
              buttonsStyling: false,
              customClass: { confirmButton: 'btn btn-light-primary' },
            });
            return;
          }
        }

        if (submitButton.value) {
          // eslint-disable-next-line
          submitButton.value!.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }
        // await store.dispatch(Actions.GET_REGISTERED_TRADE_REPORTS, values);
        // data.value = store.getters.registeredTradesReports;
        isLoading.value = true;
        hasQueried.value = true;
        await store.dispatch(
          Actions.GET_REGISTERED_TRADE_PER_QUARTER_REPORTS,
          selectedItems
        );

        data.value = store.getters.registeredTradesReportsPerQuarter;
        isLoading.value = false;

        if (data.value.data.length > 0) showExportBtn.value = true;

        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          isLoading.value = false;
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          //Deactivate indicator
          submitButton.value?.removeAttribute('data-kt-indicator');
          // eslint-disable-next-line
          submitButton.value!.disabled = false;
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_GET_REPORTS_DATA'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            //Deactivate indicator
            submitButton.value?.removeAttribute('data-kt-indicator');
            // eslint-disable-next-line
            submitButton.value!.disabled = false;

            // router.push({ name: 'ad-types-listing' });
          });
        }
      };

      onMounted(() => {
        setCurrentPageBreadcrumbs(
          translate('registeredTradesPerQuarterReports'),
          [translate('Reports')]
        );
      });

      const excelHeader = reactive({
        description: 'description_ar',
        NumberOfTrades: '_count.id',
        TotalPrice: '_sum.price',
        AvgPrice: '_avg.price',
        maxPrice: '_max.price',
        minPrice: '_min.price',
      });

      const excelHeaderRows = computed(() => {
        const all = 'الكل';
        const rows: string[] = [];

        const from = selectedItems.value.dateFrom || '-';
        const to = selectedItems.value.dateTo || '-';
        rows.push(`التاريخ: من ${from} إلى ${to}`);

        if (selectedItems.value.selectedGov?.length > 0) {
          const labels = selectedItems.value.selectedGov
            .map((id) => {
              const g = govs.value?.find((gov) => gov.id === id);
              return g ? g.titleAr : id;
            })
            .join(', ');
          rows.push(`المحافظة: ${labels}`);
        } else {
          rows.push(`المحافظة: ${all}`);
        }

        if (
          selectedItems.value.selectedPropertyStatus &&
          selectedItems.value.selectedPropertyStatus.length > 0
        ) {
          const labels = selectedItems.value.selectedPropertyStatus
            .map((id) => {
              const item = registeredTradesDesc.value.find((d) => d.id === id);
              return item ? item.titleAr : id;
            })
            .join(', ');
          rows.push(`حالة العقار: ${labels}`);
        } else {
          rows.push(`حالة العقار: ${all}`);
        }

        if (selectedItems.value.propertyGroupingId) {
          const grouping = propertyGroupings.value.find(
            (g) => g.id === selectedItems.value.propertyGroupingId
          );
          rows.push(`تصنيف العقار: ${grouping ? grouping.titleAr : all}`);
        } else {
          rows.push(`تصنيف العقار: ${all}`);
        }

        const hasIncludedAreas =
          selectedItems.value.includedAreas &&
          selectedItems.value.includedAreas.length > 0;
        const hasExcludedAreas =
          selectedItems.value.selectedArea &&
          selectedItems.value.selectedArea.length > 0;

        if (hasIncludedAreas) {
          const labels = selectedItems.value.includedAreas
            .map((id) => {
              const item = areasComputed.value.find((a) => a.id === id);
              return item ? item.titleAr : id;
            })
            .join(', ');
          rows.push(`المناطق: ${labels}`);
        } else {
          rows.push(`المناطق: ${all}`);
        }

        if (hasIncludedAreas && hasExcludedAreas) {
          rows.push(`المناطق المستبعدة: تم تجاهلها`);
        } else if (hasExcludedAreas) {
          const labels = selectedItems.value.selectedArea
            .map((id) => {
              const item = areasComputed.value.find((a) => a.id === id);
              return item ? item.titleAr : id;
            })
            .join(', ');
          rows.push(`المناطق المستبعدة: ${labels}`);
        } else {
          rows.push(`المناطق المستبعدة: ${all}`);
        }

        if (
          selectedItems.value.selectedPropertyType &&
          selectedItems.value.selectedPropertyType.length > 0
        ) {
          const labels = selectedItems.value.selectedPropertyType
            .map((id) => {
              const item = propertyTypesComputed.value.find((p) => p.id === id);
              return item ? item.titleAr : id;
            })
            .join(', ');
          rows.push(`أنواع العقار المستبعدة: ${labels}`);
        } else {
          rows.push(`أنواع العقار المستبعدة: ${all}`);
        }

        if (
          selectedItems.value.status &&
          selectedItems.value.status.length > 0
        ) {
          const labels = selectedItems.value.status
            .map((val) => TradeStatusLabels[val] || val)
            .join(', ');
          rows.push(`الحالة: ${labels}`);
        } else {
          rows.push(`الحالة: ${all}`);
        }

        if (selectedItems.value.statusNotesIds?.length > 0) {
          const labels = selectedItems.value.statusNotesIds
            .map((val) => StatusNotesLabels[val] || val)
            .join(', ');
          rows.push(`ملاحظات الحالة: ${labels}`);
        } else {
          rows.push(`ملاحظات الحالة: ${all}`);
        }

        return rows;
      });

      reinitializeComponents();
      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');
      // await store.dispatch(Actions.GET_REGISTERED_TRADES_DESC);
      await store.dispatch(Actions.GET_REGISTERED_TRADES_ASSETS, 6);
      await store.dispatch(Actions.GET_ALL_AREAS_LIST, {
        type: 'registeredTrades',
      });
      store.dispatch(Actions.GET_ALL_PROPERTY_TYPE, {
        type: 'registeredTrades',
      });
      await store.dispatch(Actions.GET_ALL_REGISTERED_TRADES_GROUPINGS);
      await store.dispatch(Actions.GET_ALL_GOV_LIST);
      return {
        propertyTypes: propertyTypesComputed,
        registeredTradesDesc,
        areas: areasComputed,
        excelHeader,
        excelHeaderRows,
        dateNow: new Date().toISOString().slice(0, 10),
        showExportBtn,
        data,
        selectedItems,
        onSubmitCreate,
        submitButton,
        translate,
        goBack,
        can,
        statusOptions,
        statusNotesOptions,
        isLoading,
        hasQueried,
        resetFilters,
        govs,
        propertyGroupings,
      };
    },
  });
